import React, { useEffect, useState } from "react";
import { gql, useMutation, useQuery } from "@apollo/client";
import { useAuthz } from "../../auth/useAuthz";
import Loader from "../../Loader";
import { formatRupees, getAttachmentLink } from "./paymentQueries";

const GET_BILL_SETTLEMENTS = gql`
  query getBillSettlements($data: BillSettlementFilter) {
    getBillSettlements(data: $data) {
      id
      dealerName
      amount
      date
      paymentMethod
      reference
      attachmentUrl
      payerName
      description
      bills {
        id
        amount
        billDate
        attachmentUrl
      }
    }
  }
`;

const DELETE_BILL_SETTLEMENT = gql`
  mutation deleteLinkedPayment($id: ID!) {
    deleteBillSettlement(id: $id) {
      status
      message
    }
  }
`;

const formatDate = (value) =>
  value ? new Date(value).toLocaleDateString("en-IN") : "-";

const matchesSearch = (settlement, search) => {
  const query = search.trim().toLowerCase();
  if (!query) return true;

  const amountQuery = query.replace(/[₹,\s]/g, "");

  return (
    [
      settlement.dealerName,
      settlement.reference,
      settlement.payerName,
      settlement.paymentMethod,
      settlement.description,
    ].some((value) => value?.toLowerCase().includes(query)) ||
    (amountQuery !== "" &&
      [settlement.amount, ...settlement.bills.map((bill) => bill.amount)].some(
        (amount) => String(amount).includes(amountQuery),
      ))
  );
};

// Payments recorded against several bills. Their amounts are already counted
// through the bills, so this list never feeds the totals above it.
const LinkedPayments = ({ filters, search, refreshKey, onChanged }) => {
  const { can } = useAuthz();
  const [expandedId, setExpandedId] = useState(null);

  const variables = {
    data: {
      startDate: filters.startDate,
      endDate: filters.endDate,
      dealerName: filters.dealerName,
      paymentMethod: filters.paymentMethod,
    },
  };
  const { data, loading, error, refetch } = useQuery(GET_BILL_SETTLEMENTS, {
    variables,
    fetchPolicy: "network-only",
  });
  const [deleteBillSettlement] = useMutation(DELETE_BILL_SETTLEMENT);

  useEffect(() => {
    if (refreshKey > 0) {
      refetch();
    }
  }, [refreshKey]);

  const settlements = (data?.getBillSettlements || []).filter((settlement) =>
    matchesSearch(settlement, search),
  );
  const total = settlements.reduce(
    (sum, settlement) => sum + Number(settlement.amount),
    0,
  );

  const handleUnlink = async (settlement) => {
    const confirmed = window.confirm(
      "Unlink these bills? Bills that were due will become due again.",
    );
    if (!confirmed) {
      return;
    }

    try {
      const res = await deleteBillSettlement({
        variables: { id: settlement.id },
      });
      const result = res.data.deleteBillSettlement;

      if (result.status === "SUCCESS") {
        refetch();
        onChanged?.();
      } else {
        alert(result.message);
      }
    } catch (err) {
      console.error("Unlink failed:", err);
      alert("Unable to unlink the bills. Please try again.");
    }
  };

  if (loading) {
    return <Loader />;
  }

  if (error) {
    return (
      <div className="rounded-lg border border-red-200 bg-white p-8 text-center text-red-600">
        Error fetching linked payments.
      </div>
    );
  }

  if (settlements.length === 0) {
    return (
      <div className="rounded-lg border border-dashed border-gray-300 bg-white p-8 text-center text-gray-500">
        No linked payments found. Select bills in Inventory Purchase and use
        Record Payment to add one.
      </div>
    );
  }

  const renderActions = (settlement) => (
    <>
      {settlement.attachmentUrl && (
        <a
          target="_blank"
          rel="noreferrer"
          href={getAttachmentLink(settlement.attachmentUrl)}
          onClick={(event) => event.stopPropagation()}
          className="px-3 py-1 text-sm bg-[#333] text-gray-200 rounded hover:bg-[#444] transition whitespace-nowrap"
        >
          View Proof
        </a>
      )}
      {can("payments.edit") && (
        <button
          type="button"
          onClick={(event) => {
            event.stopPropagation();
            handleUnlink(settlement);
          }}
          className="px-3 py-1 text-sm bg-white text-red-600 rounded hover:bg-red-50 transition"
        >
          Unlink
        </button>
      )}
    </>
  );

  const renderBills = (settlement) => (
    <div className="space-y-1 text-sm text-gray-300">
      {settlement.description && (
        <p className="mb-2">
          <span className="font-semibold">Description:</span>{" "}
          {settlement.description}
        </p>
      )}
      {settlement.bills.map((bill) => (
        <div key={bill.id} className="flex items-center gap-4">
          <span className="w-40">Bill {formatDate(bill.billDate)}</span>
          <span className="w-28">{formatRupees(bill.amount)}</span>
          {bill.attachmentUrl && (
            <a
              target="_blank"
              rel="noreferrer"
              href={getAttachmentLink(bill.attachmentUrl)}
              className="text-[#FBB033] underline"
            >
              View Bill
            </a>
          )}
        </div>
      ))}
    </div>
  );

  return (
    <div className="flex flex-col space-y-4 w-full">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-2xl font-bold text-[#FBB033]">Linked Payments</h2>
        <p className="text-sm text-gray-300">
          {settlements.length} payments, {formatRupees(total)}
          <span className="ml-2 text-gray-500">
            (already counted in the bills' debit)
          </span>
        </p>
      </div>

      {/* Desktop: table */}
      <div className="hidden md:block rounded-lg overflow-x-auto bg-[#0c0b0b] p-4">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-[#1a1a1a] border-b border-[#333] text-gray-300 text-left">
              <th className="px-4 py-3">Payment Date</th>
              <th className="px-4 py-3">Dealer</th>
              <th className="px-4 py-3">Amount</th>
              <th className="px-4 py-3">Bills</th>
              <th className="px-4 py-3">Method</th>
              <th className="px-4 py-3">UTR / Reference</th>
              <th className="px-4 py-3">Payer</th>
              <th className="px-4 py-3 text-center">Actions</th>
            </tr>
          </thead>
          <tbody>
            {settlements.map((settlement) => (
              <React.Fragment key={settlement.id}>
                <tr
                  className="border-b border-[#333] hover:bg-[#1a1a1a] transition cursor-pointer text-gray-300"
                  onClick={() =>
                    setExpandedId((current) =>
                      current === settlement.id ? null : settlement.id,
                    )
                  }
                >
                  <td className="px-4 py-3 whitespace-nowrap">
                    {formatDate(settlement.date)}
                  </td>
                  <td className="px-4 py-3">{settlement.dealerName || "-"}</td>
                  <td className="px-4 py-3 font-semibold text-red-600 whitespace-nowrap">
                    {formatRupees(settlement.amount)}
                  </td>
                  <td className="px-4 py-3">{settlement.bills.length}</td>
                  <td className="px-4 py-3">{settlement.paymentMethod}</td>
                  <td className="px-4 py-3">{settlement.reference || "-"}</td>
                  <td className="px-4 py-3">{settlement.payerName || "-"}</td>
                  <td className="px-4 py-3">
                    <div className="flex justify-center gap-2">
                      {renderActions(settlement)}
                    </div>
                  </td>
                </tr>
                {expandedId === settlement.id && (
                  <tr className="bg-[#1a1a1a] border-b border-[#333]">
                    <td colSpan={8} className="px-4 py-4">
                      {renderBills(settlement)}
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile: card view */}
      <div className="md:hidden flex flex-col gap-3">
        {settlements.map((settlement) => (
          <div
            key={settlement.id}
            className="bg-[#0c0b0b] border border-[#2a2a2a] rounded-xl p-4 text-sm"
          >
            <div className="flex justify-between gap-2">
              <span className="text-white font-semibold">
                {settlement.dealerName || "-"}
              </span>
              <span className="text-red-500 font-semibold">
                {formatRupees(settlement.amount)}
              </span>
            </div>
            <p className="text-gray-400 mt-1">
              {formatDate(settlement.date)} · {settlement.paymentMethod} ·{" "}
              {settlement.bills.length} bills
              {settlement.reference ? ` · UTR ${settlement.reference}` : ""}
            </p>
            <div className="mt-3 bg-[#1a1a1a] rounded-xl p-3 border border-[#2a2a2a]">
              {renderBills(settlement)}
            </div>
            <div className="flex gap-2 mt-3">{renderActions(settlement)}</div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default LinkedPayments;
